import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Database, CheckCircle, XCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Connectors = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    broker_host: '',
    broker_port: 1883,
    topic: '',
    username: '',
    password: '',
  });

  const queryClient = useQueryClient();

  // Fetch connectors
  const { data: connectors = [], isLoading } = useQuery({
    queryKey: ['connectors'],
    queryFn: async () => {
      const response = await axios.get(`${API}/connectors`);
      return response.data;
    },
    refetchInterval: 5000,
  });

  // Create connector mutation
  const createMutation = useMutation({
    mutationFn: async (data) => {
      const response = await axios.post(`${API}/connectors`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['connectors']);
      setIsDialogOpen(false);
      setFormData({
        name: '',
        broker_host: '',
        broker_port: 1883,
        topic: '',
        username: '',
        password: '',
      });
      toast.success('Connector created successfully');
    },
    onError: (error) => {
      toast.error(`Failed to create connector: ${error.message}`);
    },
  });

  // Delete connector mutation
  const deleteMutation = useMutation({
    mutationFn: async (connectorId) => {
      await axios.delete(`${API}/connectors/${connectorId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['connectors']);
      toast.success('Connector deleted successfully');
    },
    onError: (error) => {
      toast.error(`Failed to delete connector: ${error.message}`);
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    createMutation.mutate(formData);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'broker_port' ? parseInt(value) || 1883 : value,
    }));
  };

  return (
    <div className="space-y-6" data-testid="connectors-page">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-slate-900">Data Connectors</h2>
          <p className="text-slate-600 mt-1">Manage MQTT broker connections</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-connector-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Connector
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Add MQTT Connector</DialogTitle>
              <DialogDescription>
                Configure a new MQTT broker connection for receiving telemetry data
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="connector-form">
              <div className="space-y-2">
                <Label htmlFor="name">Connector Name</Label>
                <Input
                  id="name"
                  name="name"
                  data-testid="connector-name-input"
                  placeholder="My MQTT Broker"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="broker_host">Broker Host</Label>
                  <Input
                    id="broker_host"
                    name="broker_host"
                    data-testid="broker-host-input"
                    placeholder="broker.hivemq.com"
                    value={formData.broker_host}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="broker_port">Port</Label>
                  <Input
                    id="broker_port"
                    name="broker_port"
                    data-testid="broker-port-input"
                    type="number"
                    placeholder="1883"
                    value={formData.broker_port}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="topic">MQTT Topic</Label>
                <Input
                  id="topic"
                  name="topic"
                  data-testid="topic-input"
                  placeholder="smartpallet/+/telemetry"
                  value={formData.topic}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="username">Username (Optional)</Label>
                  <Input
                    id="username"
                    name="username"
                    data-testid="username-input"
                    placeholder="username"
                    value={formData.username}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password (Optional)</Label>
                  <Input
                    id="password"
                    name="password"
                    data-testid="password-input"
                    type="password"
                    placeholder="password"
                    value={formData.password}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                  data-testid="cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={createMutation.isPending}
                  data-testid="submit-connector-btn"
                >
                  {createMutation.isPending ? 'Creating...' : 'Create Connector'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Connectors Grid */}
      {isLoading ? (
        <div className="text-center py-12">
          <p className="text-slate-500">Loading connectors...</p>
        </div>
      ) : connectors.length === 0 ? (
        <Card>
          <CardContent className="pt-12 pb-12">
            <div className="text-center">
              <Database className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No connectors configured</h3>
              <p className="text-slate-600 mb-6">Get started by adding your first MQTT connector</p>
              <Button onClick={() => setIsDialogOpen(true)} data-testid="empty-state-add-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Connector
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {connectors.map((connector) => (
            <Card key={connector.id} data-testid={`connector-${connector.id}`}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Database className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{connector.name}</CardTitle>
                      <CardDescription className="flex items-center gap-2 mt-1">
                        {connector.status === 'connected' ? (
                          <>
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            <span className="text-green-600">Connected</span>
                          </>
                        ) : (
                          <>
                            <XCircle className="w-4 h-4 text-red-500" />
                            <span className="text-red-600">Disconnected</span>
                          </>
                        )}
                      </CardDescription>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Host:</span>
                    <span className="font-medium text-slate-900">{connector.broker_host}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Port:</span>
                    <span className="font-medium text-slate-900">{connector.broker_port}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Topic:</span>
                    <span className="font-mono text-xs text-slate-900 bg-slate-100 px-2 py-1 rounded">
                      {connector.topic}
                    </span>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-slate-200">
                  <Button
                    variant="destructive"
                    size="sm"
                    className="w-full"
                    data-testid={`delete-connector-${connector.id}`}
                    onClick={() => deleteMutation.mutate(connector.id)}
                    disabled={deleteMutation.isPending}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Connectors;
